/* eslint-disable @typescript-eslint/naming-convention */
// ---------------------------------------------------------------------------
// Human live-chat continuation — wire values and customer-facing copy.
//
// Every wire value mirrors ip-assistant `orchestrator/support_live/constants.py`
// (which in turn mirrors the Rails `ai_support_sessions` CHECK constraints).
// Nothing here is a free choice: change one side and the other must follow.
// ---------------------------------------------------------------------------
export const SUPPORT_MODE = {
    HANDOFF_REQUESTED: 'handoff_requested',
    QUEUED: 'queued',
    HUMAN: 'human',
    ENDED: 'ended',
    FAILED: 'failed'
};
/** IPA is paused (and the composer routes to the human) in these modes. */
export const ACTIVE_SUPPORT_MODES = [
    SUPPORT_MODE.HANDOFF_REQUESTED,
    SUPPORT_MODE.QUEUED,
    SUPPORT_MODE.HUMAN
];
/** A customer message can be relayed only once the provider accepted the handoff. */
export const MESSAGEABLE_SUPPORT_MODES = [SUPPORT_MODE.QUEUED, SUPPORT_MODE.HUMAN];
export const SUPPORT_CHANNEL_LIVE_CHAT = 'live_chat';
export const SUPPORT_CHANNEL_EMAIL = 'email';
export const SUPPORT_END_REASON = {
    CUSTOMER_ENDED: 'customer_ended',
    AGENT_ENDED: 'agent_ended',
    FAILED: 'failed',
    EXPIRED: 'expired'
};
/** `ai_messages.role` for a message written by a human support agent. */
export const SUPPORT_ROLE_HUMAN = 'human_support';
/** Roles that are a person's message, not an AI answer: they never get the answer action bar. */
export const SUPPORT_NO_ACTION_BAR_ROLES = [SUPPORT_ROLE_HUMAN];
/**
 * `ai_messages.role` of a row that records what the customer did with support ("You emailed
 * InspectPoint Support..."). Persisted so a reload still shows it; rendered as a status line,
 * never as an answer.
 */
export const SUPPORT_ROLE_SYSTEM = 'system';
/**
 * Which support notice a `system` history row is. The history route puts it at the TOP LEVEL of
 * the row (`row.support_notice`) and the plain text in `row.content`; it is not nested in
 * `content`. Mirrors `NOTICE_*` in ip-assistant `support_live/constants.py`.
 */
export const SUPPORT_NOTICE = {
    CHAT_STARTED: 'chat_started',
    EMAIL_SENT: 'email_sent',
    CHAT_ENDED: 'chat_ended'
};
/**
 * `state` of a `support_handoff_offer` block replayed from history (B46): `offered` is still
 * redeemable and renders the normal card; `expired` (expired, already used, or unreadable) renders
 * a muted non-actionable line. Mirrors `OFFER_STATE_*` in ip-assistant `support_live/constants.py`.
 */
export const SUPPORT_OFFER_STATE = {
    OFFERED: 'offered',
    EXPIRED: 'expired'
};
/** Value of the origin marker stamped on every support-conversation row. */
export const SUPPORT_MESSAGE_ORIGIN = 'human_support';
/**
 * Where the origin marker lives. History returns `content` only (the orchestrator does not
 * serialize row `metadata`), so persisted rows carry `content.support_origin`; live SSE
 * payloads carry `origin`. Read both.
 */
export const SUPPORT_CONTENT_KEY_ORIGIN = 'support_origin';
export const SUPPORT_PAYLOAD_KEY_ORIGIN = 'origin';
export const SUPPORT_CONTENT_KEY_AUTHOR = 'author';
export const SUPPORT_CONTENT_KEY_HIDDEN = 'hidden';
export const SUPPORT_CONTENT_KEY_UNAVAILABLE_ATTACHMENTS = 'unavailable_attachments';
/** Why a file the agent sent could not be shown (the server dropped it). */
export const SUPPORT_UNAVAILABLE_REASON = {
    UNSUPPORTED_TYPE: 'unsupported_type',
    TOO_LARGE: 'too_large',
    FETCH_FAILED: 'fetch_failed'
};
/**
 * Antivirus verdict on a support attachment (`scan_status`). `null` / absent means the server
 * attached no verdict, which is how a file the orchestrator already scanned reaches the widget.
 */
export const SUPPORT_SCAN_STATUS = {
    CLEAN: 'clean',
    PENDING: 'pending_scan',
    ERROR: 'scan_error',
    INFECTED: 'infected'
};
/** A file still being scanned is re-checked this often... */
export const SUPPORT_SCAN_RECHECK_INTERVAL_MS = 5000;
/** ...for this long, after which it is reported as one that could not be checked. */
export const SUPPORT_SCAN_RECHECK_WINDOW_MS = 120000;
/**
 * Files per customer message to a human when the server did not say (`GET /support` advertises
 * the real cap as `max_files`, from `MAX_OUTBOUND_FILES`). The widget caps its picker at that, so
 * this is only the fallback before the snapshot loads or when it carries none.
 */
export const SUPPORT_MAX_FILES_PER_MESSAGE = 3;
export const SUPPORT_AUTHOR_KIND = {
    AGENT: 'agent',
    SYSTEM: 'system',
    CUSTOMER: 'customer'
};
export const SUPPORT_SSE_EVENT = {
    MESSAGE: 'support_message',
    STATE: 'support_state',
    DELIVERY: 'support_delivery',
    /** Live twin of a history notice row (B41). Not replayed on reconnect: history is its durable copy. */
    NOTICE: 'support_notice'
};
/** Delivery state of a customer message sent to a human. */
export const SUPPORT_DELIVERY = {
    SENDING: 'sending',
    SENT: 'sent',
    FAILED: 'failed'
};
/** Stable `error` codes the orchestrator support routes answer with. */
export const SUPPORT_ERROR = {
    SESSION_ACTIVE: 'SUPPORT_SESSION_ACTIVE',
    SESSION_NOT_ACTIVE: 'SUPPORT_SESSION_NOT_ACTIVE',
    MODE_VERSION_CONFLICT: 'MODE_VERSION_CONFLICT',
    ACTIVE_SESSION_EXISTS: 'ACTIVE_SESSION_EXISTS',
    LIVE_CHAT_UNAVAILABLE: 'LIVE_CHAT_UNAVAILABLE',
    REJOIN_UNAVAILABLE: 'REJOIN_UNAVAILABLE',
    /** 422 on the email handoff: the reply address the customer typed is not usable. */
    INVALID_CONTACT_EMAIL: 'INVALID_CONTACT_EMAIL',
    /** 422 on the email handoff: the user has no address on file and none was sent. */
    CONTACT_EMAIL_REQUIRED: 'CONTACT_EMAIL_REQUIRED',
    /** 409 on the attachment route: the file is still being scanned. */
    SCAN_PENDING: 'SCAN_PENDING',
    /** 409 on the attachment route: the scan failed or found something; the file is never served. */
    SCAN_FAILED: 'SCAN_FAILED',
    /** 422 on a customer message: more files than `max_files`. Nothing was sent. */
    TOO_MANY_FILES: 'TOO_MANY_FILES'
};
/**
 * How the attachment route serves a file. `inline` is for previewing (an image, a pdf) inside the
 * widget; `attachment` is the Download link: the response carries `Content-Disposition:
 * attachment`, so a browser downloads it and an iOS webview hands it to its native download
 * handler. Download is a plain link to that URL, never a window opened ahead of the fetch.
 */
export const SUPPORT_ATTACHMENT_DISPOSITION = { INLINE: 'inline', ATTACHMENT: 'attachment' };
/**
 * `available.reason` values the widget words itself. Mirrors `REASON_*` in ip-assistant
 * `support_live/constants.py`; every other reason (hours, kill switch, agents offline) reads as
 * "not right now", which is the default copy.
 */
export const SUPPORT_AVAILABILITY_REASON = {
    /** The customer's organization is redirected to its own IT team: permanent, not "closed right now". */
    ORG_RESTRICTED: 'org_restricted'
};
export const HTTP_STATUS_UNAUTHORIZED = 401;
export const HTTP_STATUS_CONFLICT = 409;
export const HTTP_STATUS_NOT_FOUND = 404;
export const HTTP_STATUS_UNPROCESSABLE = 422;
/** Longest reply address the server accepts (`CONTACT_EMAIL_MAX_CHARS`). */
export const SUPPORT_CONTACT_EMAIL_MAX_CHARS = 254;
/** Client-side shape check only (something@something.tld); the server is the authority. */
export const SUPPORT_CONTACT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/**
 * Where a file the widget itself uploaded lives, for the existing `/api/files/{ref}/refresh-url`
 * path. A customer's own attachment is served through it whenever the support attachment route
 * does not know the file (an upload still in flight is not yet part of a persisted message).
 */
export const SUPPORT_UPLOAD_DOWNLOAD_SOURCE = 'ai_generated_file';
/** Shown when the provider gave no usable agent display name. */
export const SUPPORT_DEFAULT_AUTHOR_NAME = 'InspectPoint Support';
/**
 * A fetched attachment URL is reused only while it has more than this long left. Support
 * attachment URLs are short-lived, so a URL about to expire is refetched rather than handed out.
 */
export const SUPPORT_ATTACHMENT_URL_SKEW_MS = 30000;
/**
 * The support stream's heartbeat is an SSE comment every 15s (`SSE_HEARTBEAT_SECONDS`), which no
 * event handler sees. The widget watches the raw bytes: this long with none while the tab is
 * visible and the connection is treated as dead (a NAT or a sleeping radio drops it silently).
 */
export const SUPPORT_STREAM_IDLE_TIMEOUT_MS = 45000;
/** How often the watchdog looks. */
export const SUPPORT_STREAM_WATCHDOG_TICK_MS = 5000;
/**
 * Unread counts ride on the orchestrator's `GET /api/chat` (`support_unread_count` per row), which
 * is cursor-paged, newest-created first, at most 100 rows a page. The drawer shows its own list
 * (Rails chat sessions), so the read pages until every conversation the drawer shows has been
 * seen, bounded so a very long history can never turn one refresh into an unbounded loop.
 */
export const SUPPORT_UNREAD_LIST_PAGE_SIZE = 100;
/** Pages read at most per refresh: 10 x 100 = the 1,000 newest conversations. */
export const SUPPORT_UNREAD_LIST_MAX_PAGES = 10;
/**
 * Past support chats shown in the drawer: the newest few. Every chat also lives in its own
 * conversation in the Conversations list (and its search), so this is a shortcut, not the only door.
 */
export const SUPPORT_PAST_CHATS_DRAWER_LIMIT = 5;
/** The list is short and only worth showing fresh when the drawer is opened; this folds a burst of reads. */
export const SUPPORT_PAST_CHATS_STALE_MS = 30000;
/** Bound on the SSE event-id dedupe set so a long-lived stream cannot grow without limit. */
export const SUPPORT_EVENT_DEDUPE_LIMIT = 500;
// ---------------------------------------------------------------------------
// Copy. Deliberately no wait-time promise anywhere: live chat is queued, not
// instantly available (contract: "no immediate-availability promise"), and
// nothing here implies the support issue is resolved when the chat ends.
// ---------------------------------------------------------------------------
const tooManyFilesText = (max) => `You can attach up to ${max} ${max === 1 ? 'file' : 'files'} to a chat message.`;
export const SUPPORT_COPY = {
    OFFER_TITLE: 'Talk to our support team',
    OFFER_BODY: "We'll share this conversation with the InspectPoint Support team so you don't have to repeat yourself.",
    OFFER_CHAT_LABEL: 'Chat now',
    OFFER_EMAIL_LABEL: 'Email us instead',
    OFFER_EMAIL_ONLY_LABEL: 'Email support',
    OFFER_CHAT_NOTE: "You'll be connected with the next available agent.",
    OFFER_EMAIL_INPUT_LABEL: 'Where should we reply?',
    OFFER_EMAIL_INVALID: 'Enter a valid email address, like name@company.com.',
    OFFER_EMAIL_REQUIRED: 'Enter an email address so we can reply.',
    /** B24: an open request exists; the offer is still made and the new message joins it. */
    OFFER_OPEN_TICKET_NOTE: (ticketNumber) => `You already have an open request with us${ticketNumber ? ` (#${ticketNumber})` : ''} \u2014 we'll add this to it.`,
    OFFER_EMAIL_NOTE: (email) => email ? `We'll reply by email to ${email}.` : "We'll reply by email.",
    OFFER_CLOSED_BODY: (hoursNote, email) => `${hoursNote ? `Our team is available ${hoursNote}.` : "Our team isn't available right now."} ${email ? `Leave a message and we'll email you at ${email}.` : "Leave a message and we'll email you."}`,
    OFFER_CHAT_STARTED: 'Chat started with InspectPoint Support.',
    OFFER_EMAIL_SENT: (email) => `Sent to InspectPoint Support. ${email ? `We'll reply to ${email}.` : "We'll reply by email."}`,
    OFFER_RETRY_ERROR: "We couldn't reach InspectPoint Support just now. Please try again.",
    BANNER_WAITING: 'Waiting for an agent…',
    BANNER_WAITING_DETAIL: 'Your conversation has been shared with InspectPoint Support.',
    BANNER_HUMAN: 'Chatting with InspectPoint Support',
    BANNER_ENDED: 'Chat ended',
    BANNER_ENDED_DETAIL: "You're back with IPA.",
    BANNER_ENDED_UNREAD_DETAIL: 'InspectPoint Support replied after the chat ended.',
    END_BUTTON: 'End chat and return to IPA',
    END_CONFIRM_TITLE: 'End this chat?',
    END_CONFIRM_BODY: "You'll go back to chatting with IPA. This doesn't close your support request, and InspectPoint Support can still follow up. Any reply will appear here.",
    END_CONFIRM_LABEL: 'End chat',
    END_CANCEL_LABEL: 'Keep chatting',
    END_FAILED: "We couldn't end the chat. Please try again.",
    /** The picker stopped at the cap (the draft is still in the box). */
    TOO_MANY_FILES: tooManyFilesText,
    /** A send was refused for too many files (nothing went out): the same message, plus where the text went. */
    TOO_MANY_FILES_DRAFT_KEPT: (max) => `${tooManyFilesText(max)} Your message is back in the box.`,
    /** B46: a persisted offer card that is no longer redeemable (expired or already used). */
    OFFER_EXPIRED: 'This offer has expired \u2014 ask again if you still need help.',
    LATE_REPLY_TEXT: 'InspectPoint Support replied.',
    LATE_REPLY_CONTINUE: 'Continue chat',
    LATE_REPLY_DISMISS: 'Not now',
    LATE_REPLY_CLOSED: 'This support request was closed.',
    LATE_REPLY_CLOSED_HINT: 'Message us below to start a new support chat.',
    LATE_REPLY_CLOSED_DISMISS: 'Dismiss',
    REJOIN_FAILED: "We couldn't reconnect the chat. Please try again.",
    DISMISS_LATE_FAILED: "We couldn't dismiss that just now. Please try again.",
    PAST_CHATS_SECTION: 'Support chats',
    PAST_CHAT_UNTITLED: 'Chat with InspectPoint Support',
    PAST_CHAT_BAR_TITLE: 'Past chat with InspectPoint Support',
    PAST_CHAT_BAR_ENDED: (date) => (date ? `Ended ${date}.` : 'This chat has ended.'),
    PAST_CHAT_CONTINUE: 'Continue this chat',
    PAST_CHAT_BACK: 'Back to IPA',
    PAST_CHAT_COMPOSER_PLACEHOLDER: 'Continue this chat to message InspectPoint Support\u2026',
    /** Continuing starts a chat with a person, so it follows the support hours. */
    PAST_CHAT_UNAVAILABLE: (hoursNote) => `Live chat isn't available right now${hoursNote ? ` \u2014 our team is available ${hoursNote}` : ''}.`,
    /** The org is redirected to its own IT team for support: not a temporary closure, so it says so. */
    PAST_CHAT_ORG_RESTRICTED: "Live chat with InspectPoint Support isn't available for your organization. Please contact your organization's IT team for help.",
    /** The server refused the rejoin outright (nothing to continue); not the closed-ticket case, which it handles itself. */
    PAST_CHAT_CANNOT_CONTINUE: "This chat can't be continued. Start a new conversation to reach support.",
    /** A file the agent sent that the widget cannot show: said plainly, never a silent empty bubble. */
    UNAVAILABLE_ATTACHMENT: (name, reason) => {
        const subject = `InspectPoint Support sent \u201C${name}\u201D`;
        if (reason === 'too_large')
            return `${subject}, which is too large to show here.`;
        if (reason === 'fetch_failed')
            return `${subject}, but it couldn't be loaded. Try again later.`;
        return `${subject}, which can't be shown here.`;
    },
    ATTACHMENT_LOADING: 'Loading…',
    ATTACHMENT_ERROR: "Couldn't load this file.",
    ATTACHMENT_OPEN_ERROR: "Couldn't open this file.",
    ATTACHMENT_DOWNLOAD_FAILED: "Couldn't prepare the download. Please try again.",
    ATTACHMENT_PREVIEW_FALLBACK: "Can't preview?",
    ATTACHMENT_RETRY: 'Try again',
    ATTACHMENT_DOWNLOAD: 'Download',
    ATTACHMENT_SCANNING: 'Scanning…',
    /** A file whose scan failed, found a problem, or never finished: never opened. */
    ATTACHMENT_UNCHECKED: "This file couldn't be checked \u2014 ask Support to email it.",
    ATTACHMENT_LIGHTBOX_CLOSE: 'Close',
    CONFLICT_NOTICE: "Your message wasn't sent because the chat status changed. Your message is back in the box.",
    COMPOSER_PLACEHOLDER: 'Message InspectPoint Support…',
    COMPOSER_CONNECTING_PLACEHOLDER: 'Connecting you to InspectPoint Support…',
    DELIVERY_SENDING: 'Sending…',
    DELIVERY_SENT: 'Sent',
    DELIVERY_FAILED: 'Not sent.',
    DELIVERY_RETRY: 'Retry',
    AUTHOR_LABEL: 'InspectPoint Support',
    /** Used only when a notice row arrives with no text of its own. */
    NOTICE_FALLBACK: {
        [SUPPORT_NOTICE.CHAT_STARTED]: 'You started a chat with InspectPoint Support.',
        [SUPPORT_NOTICE.EMAIL_SENT]: 'You emailed InspectPoint Support.',
        [SUPPORT_NOTICE.CHAT_ENDED]: 'Chat with InspectPoint Support ended.'
    },
    UNREAD_LABEL: (count) => count === 1 ? '1 unread reply from InspectPoint Support' : `${count} unread replies from InspectPoint Support`
};
